<template>
  <woot-modal :show.sync="show" :on-close="onClose">
    <woot-modal-header :header-title="headerTitle" :header-content="headerContent" />

    <div class="px-6 pb-6">
      <div v-if="callState === 'ringing'" class="flex flex-col items-center text-center gap-4">
        <div class="w-14 h-14 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
          <span class="text-xl font-semibold text-slate-700 dark:text-slate-200">
            {{ avatarLetter }}
          </span>
        </div>

        <div class="space-y-1">
          <div class="text-sm text-slate-500 dark:text-slate-400">
            {{ ringingLabel }}
          </div>
          <div class="text-lg font-semibold text-slate-800 dark:text-slate-100">
            {{ remoteNumber || '-' }}
          </div>
        </div>

        <div class="flex items-center justify-center gap-2 w-full">
          <template v-if="callDirection === 'inbound'">
            <woot-button
              variant="clear"
              color-scheme="secondary"
              size="expanded"
              @click="hangup"
            >
              Rechazar
            </woot-button>
            <woot-button
              variant="solid"
              color-scheme="primary"
              size="expanded"
              @click="answer"
            >
              Contestar
            </woot-button>
          </template>

          <template v-else>
            <div class="w-full space-y-2">
              <div v-if="showSipInboxSelector" class="text-left w-full">
                <label class="text-xs text-slate-500 dark:text-slate-400">
                  Línea de salida (Inbox SIP / DID)
                  <select
                    v-model="selectedSipInboxId"
                    class="mt-1 w-full border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-md px-3 py-2 text-slate-800 dark:text-slate-100"
                  >
                    <option :value="null" disabled>Selecciona un inbox</option>
                    <option
                      v-for="inbox in sipInboxes"
                      :key="inbox.id"
                      :value="Number(inbox.id)"
                    >
                      {{ inbox.name }} — {{ inbox.phone_number || '-' }}
                    </option>
                  </select>
                </label>
              </div>

              <div class="flex items-center justify-center gap-2 w-full">
                <woot-button
                  v-if="isOutboundPrepared"
                  variant="solid"
                  color-scheme="primary"
                  size="expanded"
                  :is-disabled="confirmDisabled"
                  @click="confirmDial"
                >
                  Llamar
                </woot-button>

                <woot-button
                  v-if="isOutboundPrepared"
                  variant="clear"
                  color-scheme="secondary"
                  size="expanded"
                  @click="cancelDraft"
                >
                  Cancelar
                </woot-button>

                <woot-button
                  v-else
                  variant="solid"
                  color-scheme="alert"
                  size="expanded"
                  @click="hangup"
                >
                  Colgar
                </woot-button>
              </div>
            </div>
          </template>
        </div>
      </div>

      <div v-else-if="callState === 'connected'" class="flex flex-col gap-4">
        <div class="flex items-center justify-between">
          <div class="space-y-1">
            <div class="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
              En llamada
            </div>
            <div class="text-lg font-semibold text-slate-800 dark:text-slate-100">
              {{ remoteNumber || '-' }}
            </div>
          </div>
          <div class="font-mono text-2xl text-slate-800 dark:text-slate-100 tabular-nums">
            {{ formattedDuration }}
          </div>
        </div>

        <div class="flex items-center justify-center gap-2 w-full">
          <woot-button
            variant="smooth"
            color-scheme="secondary"
            size="expanded"
            :class-names="isMuted ? 'bg-amber-500 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-100'"
            @click="toggleMute"
          >
            {{ isMuted ? 'Activar micrófono' : 'Silenciar' }}
          </woot-button>

          <woot-button
            variant="solid"
            color-scheme="alert"
            size="expanded"
            @click="hangup"
          >
            Colgar
          </woot-button>
        </div>
      </div>
    </div>
  </woot-modal>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'CallModal',
  computed: {
    ...mapGetters({
      isCallModalOpen: 'telephony/isCallModalOpen',
      callState: 'telephony/callState',
      callDirection: 'telephony/callDirection',
      remoteNumber: 'telephony/remoteNumber',
      callDuration: 'telephony/callDuration',
      isMuted: 'telephony/isMuted',
      outboundStage: 'telephony/outboundStage',
      outboundSelectedSipInboxIdFromStore: 'telephony/outboundSelectedSipInboxId',
      outboundNeedsInboxSelection: 'telephony/outboundNeedsInboxSelection',
      sipInboxes: 'telephony/availableSipInboxes',
    }),
    isOutboundPrepared() {
      return this.callDirection === 'outbound' && this.callState === 'ringing' && this.outboundStage === 'prepared';
    },
    showSipInboxSelector() {
      return this.isOutboundPrepared && this.outboundNeedsInboxSelection;
    },
    selectedSipInboxId: {
      get() {
        return this.outboundSelectedSipInboxIdFromStore ?? null;
      },
      set(val) {
        this.$store.commit('telephony/SET_OUTBOUND_SELECTED_SIP_INBOX_ID', val);
      },
    },
    confirmDisabled() {
      if (!this.isOutboundPrepared) return false;
      if (!this.outboundNeedsInboxSelection) return false;
      return !this.selectedSipInboxId;
    },
    show: {
      get() {
        return this.isCallModalOpen;
      },
      set(val) {
        // el cierre se maneja por el store al terminar llamada
      },
    },
    headerTitle() {
      if (this.callState === 'ringing') {
        if (this.callDirection === 'outbound' && this.outboundStage === 'prepared') {
          return 'Nueva llamada';
        }
        return this.callDirection === 'outbound' ? 'Llamando…' : 'Llamada entrante';
      }
      if (this.callState === 'connected') return 'En llamada';
      return 'Llamada';
    },
    ringingLabel() {
      if (this.callDirection === 'outbound' && this.outboundStage === 'prepared') {
        return this.outboundNeedsInboxSelection ? 'Selecciona la línea de salida' : 'Listo para llamar';
      }
      return this.callDirection === 'outbound' ? 'Llamando…' : 'Llamada entrante';
    },
    headerContent() {
      return this.remoteNumber || '';
    },
    avatarLetter() {
      const n = (this.remoteNumber || '').toString().trim();
      return n ? n.slice(0, 1).toUpperCase() : '?';
    },
    formattedDuration() {
      const total = Number(this.callDuration || 0);
      const m = Math.floor(total / 60)
        .toString()
        .padStart(2, '0');
      const s = Math.floor(total % 60)
        .toString()
        .padStart(2, '0');
      return `${m}:${s}`;
    },
  },
  methods: {
    onClose() {
      // Al cerrar con la "X": si aún no se marcó (draft), cancelamos; si hay sesión, colgamos.
      if (this.isOutboundPrepared) {
        this.$store.dispatch('telephony/CANCEL_DRAFT');
        return;
      }
      this.$store.dispatch('telephony/HANGUP');
    },
    answer() {
      this.$store.dispatch('telephony/ANSWER');
    },
    hangup() {
      this.$store.dispatch('telephony/HANGUP');
    },
    confirmDial() {
      this.$store.dispatch('telephony/CONFIRM_DIAL');
    },
    cancelDraft() {
      this.$store.dispatch('telephony/CANCEL_DRAFT');
    },
    toggleMute() {
      this.$store.dispatch('telephony/TOGGLE_MUTE');
    },
  },
};
</script>

